import { Box, Stack, Avatar, Typography, Divider, IconButton, Dialog, TextField, Button, CircularProgress } from '@mui/material';
import { Schema } from '../../../../../amplify/data/resource';
import { Icon } from '@iconify/react';
import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import Select from '../../../custom/Select';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';

// @ts-ignore
type Client = Schema['User']['type'];

interface BankOption {
  label: string;
  value: string;
}

interface BranchOption {
  label: string;
  value: string;
  number: number;
}

interface UserFormData {
  companyName: string;
  authorizedSignatoryName: string;
  fullName: string;
  userId: string;
  companyRegistrationNumber: string;
  email: string;
  phone: string;
  bankNumber: string;
  branchNumber: string;
  accountNumber: string;
}

const apiClient = generateClient<Schema>();

const Main = ({ 
  client, 
  onPageChange, 
  onDialogStateChange 
}: { 
  client: Client; 
  onPageChange: (page: string) => void;
  onDialogStateChange?: (isOpen: boolean) => void;
}) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [currentClient, setCurrentClient] = useState(client);
  const [userData, setUserData] = useState({
    companyName: client?.companyName || '',
    authorizedSignatoryName: client?.authorizedSignatoryName || client?.fullName || '',
    fullName: client?.fullName || '',
    userId: client?.userId || '',
    companyRegistrationNumber: client?.companyRegistrationNumber || '',
    email: client?.email || '',
    phone: client?.phone || '',
    bankNumber: client?.paymentInfo?.bank || '',
    branchNumber: client?.paymentInfo?.branch || '',
    accountNumber: client?.paymentInfo?.account || '',
  });

  // Update local state when client changes
  useEffect(() => {
    setCurrentClient(client);
    setUserData({
      companyName: client?.companyName || '',
      authorizedSignatoryName: client?.authorizedSignatoryName || client?.fullName || '',
      fullName: client?.fullName || '',
      userId: client?.userId || '',
      companyRegistrationNumber: client?.companyRegistrationNumber || '',
      email: client?.email || '',
      phone: client?.phone || '',
      bankNumber: client?.paymentInfo?.bank || '',
      branchNumber: client?.paymentInfo?.branch || '',
      accountNumber: client?.paymentInfo?.account || '',
    });
  }, [client]);

  const handleEditClick = () => {
    setEditDialogOpen(true);
    setUpdateError(null);
    setUpdateSuccess(false);
    onDialogStateChange?.(true);
  };

  const handleSave = async () => {
    if (!currentClient?.id) {
      setUpdateError('שגיאה: לא נמצא מזהה משתמש');
      return;
    }

    setIsUpdating(true);
    setUpdateError(null);
    setUpdateSuccess(false);

    try {
      const updateData = {
        id: currentClient.id,
        companyName: userData.companyName,
        authorizedSignatoryName: userData.authorizedSignatoryName,
        fullName: userData.fullName,
        userId: userData.userId,
        companyRegistrationNumber: userData.companyRegistrationNumber,
        email: userData.email,
        phone: userData.phone,
        paymentInfo: {
          bank: userData.bankNumber,
          branch: userData.branchNumber,
          account: userData.accountNumber,
          type: currentClient?.paymentInfo?.type || 'BUSINESS',
        },
      };

      const result = await apiClient.models.User.update(updateData);
      
      if (result.data) {
        // Update local state with new data
        setCurrentClient(result.data);
        setUpdateSuccess(true);
        
        // Close dialog after short delay to show success message
        setTimeout(() => {
          setEditDialogOpen(false);
          setUpdateSuccess(false);
          onDialogStateChange?.(false);
        }, 1500);
      } else {
        throw new Error('לא ניתן לקבל נתונים מעודכנים');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setUpdateError(error instanceof Error ? error.message : 'אירעה שגיאה בשמירת הנתונים');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setUserData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear errors when data changes
    if (updateError) {
      setUpdateError(null);
    }
  };

  return (
    <>
      <Stack justifyContent={'space-between'} spacing={20} sx={{ width: '100%', flex: 1 }}>
        <Stack spacing={3} sx={{ width: '100%', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
          <UserProfile client={currentClient} />
          <UserDetails client={currentClient} onEditClick={handleEditClick} />
        </Stack>
        <NavigationButtons onPageChange={onPageChange} />
      </Stack>
      <EditDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          onDialogStateChange?.(false);
        }}
        userData={userData}
        onInputChange={handleInputChange}
        onSave={handleSave}
        isUpdating={isUpdating}
        updateError={updateError}
        updateSuccess={updateSuccess}
      />
    </>
  );
};

const UserProfile = ({ client }: { client: Client }) => {
  return (
    <Stack alignItems={'center'}>
      <UserAvatar fullName={client.companyName || ''} />
      <Typography fontWeight={'700'} fontSize={'16px'} fontFamily={'AlmoniDL'} color={'#424245'} textAlign={'center'}>
        {client.companyRegistrationNumber}
      </Typography>
    </Stack>
  );
};

const UserAvatar = ({ fullName }: { fullName: string }) => {
  return (
    <Stack spacing={2} sx={{ alignItems: 'center' }}>
      <Avatar
        sx={{
          borderRadius: '40px',
          width: '120px',
          height: '120px',
          bgcolor: '#DBE4EC',
          color: '#343434',
          fontWeight: '900',
          fontSize: '32px',
          fontFamily: 'Montserrat',
        }}
      >
        P
      </Avatar>
      <Typography
        fontWeight={'700'}
        fontSize={'24px'}
        fontFamily={'AlmoniDL'}
        color={'#424245'}
        textAlign="center"
      >
        {fullName || 'שם העסק'}
      </Typography>
    </Stack>
  );
};

const UserDetails = ({ client, onEditClick }: { client: Client; onEditClick: () => void }) => {
  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Stack direction={'row'} spacing={0.5} alignItems={'center'} onClick={onEditClick} sx={{ cursor: 'pointer' }}>
          <Typography sx={{ textDecoration: 'underline' }} fontFamily={'AlmoniDL'} fontSize={'12px'} color={'#595A5D'}>
            ערוך פרטים
          </Typography>
          <Icon icon='mdi:edit-outline' color='#595A5D' />
        </Stack>
        <Typography fontWeight={'700'} color={'#808080'} fontSize={'14px'} fontFamily={'AlmoniDL'} textAlign={'right'} dir='rtl'>
          פרטי מורשה חתימה:
        </Typography>
      </Stack>
      <Stack spacing={8} direction={'row-reverse'}>
        <Stack alignItems={'flex-end'}>
          <Typography fontSize={'11px'} fontFamily={'AlmoniDL'} color={'#808080'} textAlign={'right'}>
            שם מלא
          </Typography>
          <Typography fontWeight={'700'} fontSize={'13px'} fontFamily={'AlmoniDL'} color={'#424245'} textAlign={'right'}>
            {client.authorizedSignatoryName}
          </Typography>
        </Stack>
        <Stack alignItems={'flex-end'}>
          <Typography fontSize={'11px'} fontFamily={'AlmoniDL'} color={'#808080'} textAlign={'right'}>
            ת.ז
          </Typography>
          <Typography fontWeight={'700'} fontSize={'13px'} fontFamily={'AlmoniDL'} color={'#424245'} textAlign={'right'}>
            {client.userId}
          </Typography>
        </Stack>
      </Stack>
      <Stack alignItems={'flex-end'}>
        <Typography fontSize={'11px'} fontFamily={'AlmoniDL'} color={'#808080'} textAlign={'right'}>
          כתובת מייל
        </Typography>
        <Typography fontWeight={'700'} fontSize={'13px'} fontFamily={'AlmoniDL'} color={'#424245'} textAlign={'right'}>
          {client.email}
        </Typography>
      </Stack>
      <Stack alignItems={'flex-end'}>
        <Typography fontSize={'11px'} fontFamily={'AlmoniDL'} color={'#808080'} textAlign={'right'}>
          טלפון
        </Typography>
        <Typography fontWeight={'700'} fontSize={'13px'} fontFamily={'AlmoniDL'} color={'#424245'} textAlign={'right'}>
          {client.phone}
        </Typography>
      </Stack>
    </Stack>
  );
};

const NavigationButtons = ({ onPageChange }: { onPageChange: (page: string) => void }) => {
  const navigate = useNavigate();
  const handleInvoicesClick = () => {
    onPageChange('invoices');
  };

  const handleWhatsAppClick = () => {
    window.open('https://wa.link/uuw7es', '_blank');
  };

  const handleTermsClick = () => {
    window.open('https://www.pielapay.co.il/terms', '_blank');
  };

  const handlePrivacyClick = () => {
    window.open('https://www.pielapay.co.il/privacy', '_blank');
  };

  const handleSignOut = () => {
    signOut().then(() => {
      navigate('/login');
    });
  };

  return (
    <Stack spacing={2}>
      <InvoicesButton onClick={handleInvoicesClick} />
      <SupportButton onClick={handleWhatsAppClick} />
      <BottomButtonGroup
        onTermsClick={handleTermsClick}
        onPrivacyClick={handlePrivacyClick}
        onSignOutClick={handleSignOut}
      />
    </Stack>
  );
};

const InvoicesButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Stack direction={'row-reverse'} alignItems={'center'} justifyContent={'space-between'}
      sx={{ backgroundColor: '#F2F4F7', borderRadius: '12px', py: '10px', px: '20px', cursor: 'pointer' }}
      onClick={onClick}>
      <Stack direction='row' spacing={0.5} alignItems={'center'}>
        <Typography fontWeight={'700'} fontSize={'14px'} fontFamily={'AlmoniDL'} color={'#595A5D'}>
          החשבוניות שלי
        </Typography>
        <Icon icon='material-symbols:receipt-long' color='#595A5D' />
      </Stack>
      <Icon icon='material-symbols:arrow-back-ios' color='#595A5D' />
    </Stack>
  );
};

const SupportButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Stack direction={'row-reverse'} alignItems={'center'} justifyContent={'space-between'}
      sx={{ backgroundColor: '#F2F4F7', borderRadius: '12px', py: '10px', px: '20px', cursor: 'pointer' }}
      onClick={onClick}>
      <Stack direction='row' spacing={0.5} alignItems={'center'}>
        <Typography fontWeight={'700'} fontSize={'14px'} fontFamily={'AlmoniDL'} color={'#595A5D'}>
          עזרה ותמיכה
        </Typography>
        <Icon icon='ic:baseline-whatsapp' color='#595A5D' />
      </Stack>
    </Stack>
  );
};

const BottomButtonGroup = ({
  onTermsClick,
  onPrivacyClick,
  onSignOutClick
}: {
  onTermsClick: () => void;
  onPrivacyClick: () => void;
  onSignOutClick: () => void;
}) => {
  return (
    <Stack direction='row' alignItems={'center'} justifyContent={'space-between'}>
      <Typography sx={{ textDecoration: 'underline', cursor: 'pointer' }}
        fontWeight={'700'} fontSize={'14px'} fontFamily={'AlmoniDL'} color={'#595A5D'}
        onClick={onPrivacyClick}>
        מדיניות פרטיות
      </Typography>
      <Typography sx={{ textDecoration: 'underline', cursor: 'pointer' }}
        fontWeight={'700'} fontSize={'14px'} fontFamily={'AlmoniDL'} color={'#595A5D'}
        onClick={onTermsClick}>
        תנאי שימוש
      </Typography>
      <Stack direction={'row-reverse'} alignItems={'center'} justifyContent={'space-between'}
        sx={{ backgroundColor: '#F2F4F7', borderRadius: '12px', py: '10px', px: '20px', cursor: 'pointer' }}
        onClick={onSignOutClick}>
        <Stack direction='row' spacing={0.5} alignItems={'center'}>
          <Typography fontWeight={'700'} fontSize={'14px'} fontFamily={'AlmoniDL'} color={'#595A5D'}>
            יציאה
          </Typography>
          <Icon icon='mingcute:exit-line' color='#595A5D' />
        </Stack>
      </Stack>
    </Stack>
  );
};

const EditDialog = ({
  open,
  onClose,
  userData,
  onInputChange,
  onSave,
  isUpdating,
  updateError,
  updateSuccess
}: {
  open: boolean;
  onClose: () => void;
  userData: UserFormData;
  onInputChange: (field: string, value: string) => void;
  onSave: () => void;
  isUpdating: boolean;
  updateError: string | null;
  updateSuccess: boolean;
}) => {
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [branches, setBranches] = useState<BranchOption[]>([]);

  useEffect(() => {
    const fetchBanks = async () => {
      if (!open) return;

      try {
        const response = await apiClient.queries.getBanksInfo({});
        let banksList: BankOption[] = [];
        if (response?.data) {
          try {
            const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
            if (data && data.body && Array.isArray(data.body)) {
              banksList = data.body
                .map((bank: any) => ({
                  label: bank.name || '',
                  value: bank.number ? String(bank.number) : '',
                }))
                .filter((bank: BankOption) => bank.label && bank.value);
            }
          } catch (e) {
            console.error('Error processing bank data:', e);
          }
        }
        setBanks(banksList);
      } catch (error) {
        console.error('Error fetching banks:', error);
        setBanks([]);
      }
    };

    fetchBanks();
  }, [open]);

  useEffect(() => {
    const fetchBranches = async () => {
      if (!userData.bankNumber || !open) {
        setBranches([]);
        return;
      }

      try {
        const response = await apiClient.queries.getBanksInfo({
          bank: userData.bankNumber,
        });
        let branchesList: BranchOption[] = [];
        if (response?.data) {
          try {
            const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
            if (data && data.body && Array.isArray(data.body)) {
              branchesList = data.body
                .map((branch: any) => ({
                  label: branch.name ? `${branch.branch || ''} - ${branch.name}` : branch.branch || '',
                  value: branch.branch || '',
                  number: parseInt(branch.branch || '0', 10)
                }))
                .filter((branch: BranchOption) => branch.value)
                .sort((a: BranchOption, b: BranchOption) => a.number - b.number);
            }
          } catch (e) {
            console.error('Error processing branch data:', e);
          }
        }
        setBranches(branchesList);
      } catch (error) {
        console.error('Error fetching branches:', error);
        setBranches([]);
      }
    };

    fetchBranches();
  }, [userData.bankNumber, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: 0,
          maxWidth: '1000px',
          bgcolor: '#F8F9FA',
        },
      }}
    >
      <Stack sx={{ position: 'relative', p: 3 }} onClick={(e) => e.stopPropagation()}>
        <DialogHeader onClose={onClose} />
        {updateError && (
          <Stack 
            sx={{ 
              mb: 2, 
              p: 2, 
              backgroundColor: '#ffebee', 
              borderRadius: '8px',
              border: '1px solid #ffcdd2'
            }}
          >
            <Typography 
              color="#c62828" 
              fontSize="14px" 
              fontFamily="AlmoniDL" 
              textAlign="right"
            >
              {updateError}
            </Typography>
          </Stack>
        )}
        {updateSuccess && (
          <Stack 
            sx={{ 
              mb: 2, 
              p: 2, 
              backgroundColor: '#e8f5e8', 
              borderRadius: '8px',
              border: '1px solid #c8e6c9'
            }}
          >
            <Typography 
              color="#2e7d32" 
              fontSize="14px" 
              fontFamily="AlmoniDL" 
              textAlign="right"
            >
              הפרטים נשמרו בהצלחה!
            </Typography>
          </Stack>
        )}
        <DialogContent
          userData={userData}
          onInputChange={onInputChange}
          onSave={onSave}
          banks={banks}
          branches={branches}
          isUpdating={isUpdating}
          updateSuccess={updateSuccess}
        />
      </Stack>
    </Dialog>
  );
};

const DialogHeader = ({ onClose }: { onClose: () => void }) => {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
      <IconButton onClick={onClose} sx={{ p: 0.5 }}>
        <Icon icon="material-symbols:close" width="20px" color="#666" />
      </IconButton>
      <Typography fontWeight={'700'} fontSize={'18px'} fontFamily={'AlmoniDL'} textAlign="right" color={'#333'}>
        עריכת פרטי פרופיל
      </Typography>
    </Stack>
  );
};

const DialogContent = ({
  userData,
  onInputChange,
  onSave,
  banks,
  branches,
  isUpdating,
  updateSuccess
}: {
  userData: UserFormData;
  onInputChange: (field: string, value: string) => void;
  onSave: () => void;
  banks: BankOption[];
  branches: BranchOption[];
  isUpdating: boolean;
  updateSuccess: boolean;
}) => {
  return (
    <Stack direction="row" spacing={4}>
      <PersonalInfoSection 
        userData={userData} 
        onInputChange={onInputChange} 
        onSave={onSave}
        isUpdating={isUpdating}
        updateSuccess={updateSuccess}
      />
      <BusinessInfoSection
        userData={userData}
        onInputChange={onInputChange}
        banks={banks}
        branches={branches}
        isUpdating={isUpdating}
      />
    </Stack>
  );
};

const PersonalInfoSection = ({
  userData,
  onInputChange,
  onSave,
  isUpdating,
  updateSuccess
}: {
  userData: UserFormData;
  onInputChange: (field: string, value: string) => void;
  onSave: () => void;
  isUpdating: boolean;
  updateSuccess: boolean;
}) => {
  return (
    <Stack flex={1} spacing={2.5}>
      <Typography fontWeight={'700'} fontSize={'14px'} fontFamily={'AlmoniDL'} textAlign="right" color={'#666'} sx={{ mb: 1 }}>
        פרטי מורשה חתימה
      </Typography>
      
      <FormField 
        label="שם מורשה חתימה" 
        value={userData.authorizedSignatoryName} 
        onChange={(value) => onInputChange('authorizedSignatoryName', value)}
        disabled={isUpdating}
      />
      <FormField 
        label="ת.ז" 
        value={userData.userId} 
        onChange={(value) => onInputChange('userId', value)}
        disabled={isUpdating}
      />
      <FormField 
        label="כתובת מייל" 
        value={userData.email} 
        onChange={(value) => onInputChange('email', value)} 
        type="email"
        disabled={isUpdating}
      />
      <FormField 
        label="טלפון" 
        value={userData.phone} 
        onChange={(value) => onInputChange('phone', value)}
        disabled={isUpdating}
      />
      <Stack sx={{ height: '4px' }} />
      <Button
        onClick={onSave}
        variant="contained"
        fullWidth
        disabled={isUpdating || updateSuccess}
        sx={{
          bgcolor: updateSuccess ? '#4caf50' : '#7AED20',
          color: '#333',
          fontWeight: '700',
          fontSize: '16px',
          fontFamily: 'AlmoniDL',
          borderRadius: '8px',
          py: 1.5,
          mt: 0,
          textTransform: 'none',
          boxShadow: 'none',
          position: 'relative',
          '&:hover': {
            bgcolor: updateSuccess ? '#4caf50' : '#6BD91A',
            boxShadow: 'none',
          },
          '&:disabled': {
            bgcolor: updateSuccess ? '#4caf50' : '#e0e0e0',
            color: updateSuccess ? '#fff' : '#999',
          },
        }}
      >
        {isUpdating && (
          <CircularProgress 
            size={20} 
            sx={{ 
              position: 'absolute', 
              left: '20px',
              color: '#333'
            }} 
          />
        )}
        {updateSuccess ? (
          <>
            <Icon icon="material-symbols:check" style={{ marginLeft: '8px' }} />
            נשמר!
          </>
        ) : isUpdating ? (
          'שומר...'
        ) : (
          'שמירה'
        )}
      </Button>
    </Stack>
  );
};

const BusinessInfoSection = ({
  userData,
  onInputChange,
  banks,
  branches,
  isUpdating
}: {
  userData: UserFormData;
  onInputChange: (field: string, value: string) => void;
  banks: BankOption[];
  branches: BranchOption[];
  isUpdating: boolean;
}) => {
  return (
    <Stack flex={1} spacing={2.5}>
      <Typography fontWeight={'700'} fontSize={'14px'} fontFamily={'AlmoniDL'} textAlign="right" color={'#666'} sx={{ mb: 1 }}>
        פרטי עסק וחשבון
      </Typography>
      <FormField 
        label="שם העסק" 
        value={userData.companyName} 
        onChange={(value) => onInputChange('companyName', value)}
        disabled={isUpdating}
      />
      <FormField 
        label="ח.פ" 
        value={userData.companyRegistrationNumber} 
        onChange={(value) => onInputChange('companyRegistrationNumber', value)}
        disabled={isUpdating}
      />
      <SelectField
        label="בנק"
        value={userData.bankNumber}
        onChange={(value) => onInputChange('bankNumber', value)}
        options={banks}
        disabled={!banks.length || isUpdating}
      />
      <SelectField
        label="סניף"
        value={userData.branchNumber}
        onChange={(value) => onInputChange('branchNumber', value)}
        options={branches}
        disabled={!userData.bankNumber || !branches.length || isUpdating}
      />
      <FormField 
        label="מספר חשבון" 
        value={userData.accountNumber} 
        onChange={(value) => onInputChange('accountNumber', value)}
        disabled={isUpdating}
      />
    </Stack>
  );
};

const FormField = ({
  label,
  value,
  onChange,
  type = "text",
  disabled = false
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  disabled?: boolean;
}) => {
  return (
    <Stack>
      <Typography fontWeight={'600'} fontSize={'12px'} fontFamily={'AlmoniDL'} color={'#666'} textAlign="right" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        variant="outlined"
        type={type}
        disabled={disabled}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '16px',
            backgroundColor: disabled ? '#f0f0f0' : '#F2F4F7',
            height: '40px',
            fontSize: '14px',
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: 'none' },
          },
          '& .MuiOutlinedInput-input': {
            textAlign: 'right',
            fontFamily: 'AlmoniDL',
            fontWeight: '700',
            color: disabled ? '#999' : 'inherit',
          },
        }}
      />
    </Stack>
  );
};

const SelectField = ({
  label,
  value,
  onChange,
  options,
  disabled
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: (BankOption | BranchOption)[];
  disabled: boolean;
}) => {
  return (
    <Stack>
      <Typography fontWeight={'600'} fontSize={'12px'} fontFamily={'AlmoniDL'} color={'#666'} textAlign="right" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Select
        label=""
        value={value}
        onChange={(value: string) => onChange(value)}
        options={options}
        disabled={disabled}
        id={`select-${label}`}
        sx={{
          '& .MuiFilledInput-root': {
            borderRadius: '16px',
            backgroundColor: disabled ? '#f0f0f0' : '#F2F4F7',
            border: 'none',
            height: '40px',
            fontSize: '14px',
            '&:before': { display: 'none' },
            '&:after': { display: 'none' },
            '&:hover': { backgroundColor: disabled ? '#f0f0f0' : '#F2F4F7' },
            '&.Mui-focused': { backgroundColor: disabled ? '#f0f0f0' : '#F2F4F7' },
          },
          '& .MuiSelect-select': {
            textAlign: 'right',
            fontFamily: 'AlmoniDL',
            fontWeight: '700',
            color: disabled ? '#999' : 'inherit',
          },
        }}
      />
    </Stack>
  );
};

export default Main;